import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BellIcon, CheckIcon, XMarkIcon, TrashIcon } from '@heroicons/react/24/outline';
import { BellAlertIcon } from '@heroicons/react/24/solid';
import api from '../services/api';

// Custom School Bus SVG Icon
const SchoolBusIcon = ({ size = 48 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 64 64"
    role="img"
    aria-label="School bus"
    width={size}
    height={size}
  >
    {/* BUS BODY */}
    <rect x="10" y="18" width="44" height="26" rx="6" fill="#D4A94E"/>
    {/* Top sign strip */}
    <rect x="16" y="16" width="32" height="6" rx="3" fill="#C49A3E"/>
    {/* WINDOWS */}
    <rect x="15" y="22" width="10" height="12" rx="2" fill="#E5F3FF"/>
    <rect x="27" y="22" width="10" height="12" rx="2" fill="#E5F3FF"/>
    <rect x="39" y="22" width="10" height="12" rx="2" fill="#E5F3FF"/>
    {/* Window mid-line for depth */}
    <rect x="15" y="28" width="34" height="2" fill="#C7D2FE" opacity="0.85"/>
    {/* GRILL */}
    <rect x="24" y="34" width="16" height="7" rx="3" fill="#111827"/>
    <rect x="26" y="36" width="12" height="3" rx="1.5" fill="#E5E7EB"/>
    {/* HEADLIGHTS */}
    <circle cx="16" cy="37" r="3" fill="#FDE68A"/>
    <circle cx="48" cy="37" r="3" fill="#FDE68A"/>
    {/* BUMPER */}
    <rect x="14" y="40" width="36" height="6" rx="3" fill="#020617" opacity="0.95"/>
    {/* WHEELS */}
    <circle cx="20" cy="48" r="5" fill="#020617"/>
    <circle cx="44" cy="48" r="5" fill="#020617"/>
    <circle cx="20" cy="48" r="2.5" fill="#111827"/>
    <circle cx="44" cy="48" r="2.5" fill="#111827"/>
    {/* SUBTLE SHADOW */}
    <ellipse cx="32" cy="54" rx="18" ry="3" fill="#000000" opacity="0.25"/>
  </svg>
);

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    // E8 (the gate's one sanctioned bell touch): the herald's poll
    // rebroadcasts its fresh payload — adopt it so the badge stays current
    // between our own fetches. Read semantics and all behaviors unchanged.
    const onHeraldRefresh = (e) => {
      setNotifications(e.detail.notifications || []);
      setUnreadCount(e.detail.unreadCount || 0);
    };
    window.addEventListener('gvc:notifications-refreshed', onHeraldRefresh);
    return () => {
      clearInterval(interval);
      window.removeEventListener('gvc:notifications-refreshed', onHeraldRefresh);
    };
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await api.get('/notifications');
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unreadCount || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const markAsRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`, {});
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put('/notifications/read-all', {});
      fetchNotifications();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const clearAll = async () => {
    try {
      await api.delete('/notifications/clear-all');
      fetchNotifications();
      setIsOpen(false);
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  };

  const acknowledgeNotification = async (id) => {
    try {
      await api.put(`/notifications/${id}/acknowledge`, {});
      fetchNotifications();
    } catch (error) {
      console.error('Error acknowledging notification:', error);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 4: return '#F87171'; // Emergency - Red
      case 3: return '#D4A94E'; // Urgent - Gold
      case 2: return '#D4A94E'; // Priority - Gold
      default: return '#3b82f6'; // Normal - Blue
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 4: return 'EMERGENCY';
      case 3: return 'URGENT';
      case 2: return 'PRIORITY';
      default: return 'INFO';
    }
  };

  return (
    <div style={styles.container}>
      <button 
        style={styles.bellButton}
        onClick={() => setIsOpen(!isOpen)}
      >
        {unreadCount > 0 ? (
          <BellAlertIcon style={{ ...styles.bellIcon, color: '#D4A94E' }} />
        ) : (
          <BellIcon style={styles.bellIcon} />
        )}
        {unreadCount > 0 && (
          <span style={styles.badge}>{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <>
          <div style={styles.overlay} onClick={() => setIsOpen(false)} />
          <div style={styles.dropdown}>
            <div style={styles.dropdownHeader}>
              <h3 style={styles.dropdownTitle}>🔔 Notifications</h3>
              <div style={{ display: 'flex', gap: '8px' }}>
                {notifications.length > 0 && (
                  <>
                    {unreadCount > 0 && (
                      <button 
                        style={styles.headerBtn} 
                        onClick={markAllAsRead}
                        title="Mark all as read"
                      >
                        <CheckIcon style={{ width: 16, height: 16 }} />
                      </button>
                    )}
                    <button
                      style={{ ...styles.headerBtn, color: '#F87171' }}
                      onClick={clearAll}
                      title="Clear all"
                    >
                      <TrashIcon style={{ width: 16, height: 16 }} />
                    </button>
                  </>
                )}
                <button style={styles.closeBtn} onClick={() => setIsOpen(false)}>
                  <XMarkIcon style={{ width: 20, height: 20 }} />
                </button>
              </div>
            </div>

            <div style={styles.notificationList}>
              {notifications.length === 0 ? (
                <div style={styles.emptyState}>
                  <SchoolBusIcon size={64} />
                  <p style={{ marginTop: '12px' }}>No notifications yet!</p>
                </div>
              ) : (
                notifications.map((notif) => (
                  <div 
                    key={notif.id} 
                    style={{
                      ...styles.notificationItem,
                      borderLeftColor: getPriorityColor(notif.priority),
                      background: notif.is_read ? '#141414' : '#1E1E1E',
                      cursor: (!notif.is_read || notif.link) ? 'pointer' : 'default'
                    }}
                    onClick={() => {
                      if (!notif.is_read) markAsRead(notif.id);
                      if (notif.link) {
                        setIsOpen(false);
                        navigate(notif.link);
                      }
                    }}
                  >
                    <div style={styles.notificationHeader}>
                      <span style={{
                        ...styles.priorityBadge,
                        background: getPriorityColor(notif.priority)
                      }}>
                        {getPriorityLabel(notif.priority)}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {!notif.is_read && (
                          <span style={styles.unreadDot} />
                        )}
                        <span style={styles.notificationTime}>
                          {new Date(notif.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <h4 style={styles.notificationTitle}>{notif.title}</h4>
                    <p style={styles.notificationMessage}>{notif.message}</p>
                    
                    {notif.requires_acknowledgment && !notif.acknowledged_at && (
                      <button 
                        style={styles.ackButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          acknowledgeNotification(notif.id);
                        }}
                      >
                        <CheckIcon style={{ width: 16, height: 16 }} />
                        Acknowledge
                      </button>
                    )}
                    
                    {notif.acknowledged_at && (
                      <span style={styles.acknowledgedBadge}>
                        ✓ Acknowledged
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const styles = {
  container: {
    position: 'relative'
  },
  bellButton: {
    position: 'relative',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '8px'
  },
  bellIcon: {
    width: '22px',
    height: '22px',
    color: '#666666'
  },
  badge: {
    position: 'absolute',
    top: '2px',
    right: '2px',
    background: '#F87171',
    color: 'white',
    fontSize: '10px',
    fontWeight: '700',
    padding: '2px 6px',
    borderRadius: '10px',
    minWidth: '18px',
    textAlign: 'center'
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 999
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: '8px',
    width: '380px',
    maxHeight: '500px',
    background: '#1E1E1E',
    borderRadius: '16px',
    boxShadow: '0 20px 50px rgba(0, 0, 0, 0.5)',
    zIndex: 1000,
    overflow: 'hidden',
    border: '1px solid #2A2A2A'
  },
  dropdownHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid #2A2A2A',
    background: 'rgba(212,169,78,0.1)'
  },
  dropdownTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#FFFFFF',
    margin: 0
  },
  headerBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#A0A0A0',
    padding: '4px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  closeBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#A0A0A0'
  },
  notificationList: {
    maxHeight: '400px',
    overflowY: 'auto'
  },
  notificationItem: {
    padding: '16px 20px',
    borderBottom: '1px solid #2A2A2A',
    borderLeft: '4px solid',
    transition: 'background 0.2s'
  },
  notificationHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px'
  },
  priorityBadge: {
    fontSize: '10px',
    fontWeight: '700',
    color: 'white',
    padding: '2px 8px',
    borderRadius: '4px'
  },
  unreadDot: {
    width: '8px',
    height: '8px',
    background: '#3b82f6',
    borderRadius: '50%'
  },
  notificationTime: {
    fontSize: '12px',
    color: '#6B6B6B'
  },
  notificationTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#FFFFFF',
    margin: '0 0 4px 0'
  },
  notificationMessage: {
    fontSize: '13px',
    color: '#A0A0A0',
    margin: 0,
    lineHeight: '1.4'
  },
  ackButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    marginTop: '12px',
    padding: '8px 14px',
    background: '#4ADE80',
    color: '#1A1A1A',
    border: 'none',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  acknowledgedBadge: {
    display: 'inline-block',
    marginTop: '12px',
    fontSize: '12px',
    color: '#4ADE80',
    fontWeight: '600'
  },
  emptyState: {
    padding: '40px',
    textAlign: 'center',
    color: '#6B6B6B',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  }
};

export default NotificationBell;
